import React, { useMemo, useState } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import {
  getFriendlySectionTitle,
  isEmptyFieldValue,
} from '../utils/reportDisplayUtils';

interface SearchResult {
  api_id: number;
  api_name: string;
  status: 'success' | 'failed';
  data: Record<string, any>;
  raw_response: Record<string, any>;
  processing_time: number;
  error_message: string;
  display_parameters?: Record<string, any>;
  request_url?: string;
  request_body?: Record<string, unknown> | null;
  request_headers?: Record<string, unknown>;
  response_status?: number;
  response_headers?: Record<string, unknown>;
}

interface ReportSectionFieldColumn {
  path: string;
  display_name: string;
}

interface ReportSectionField {
  display_name: string;
  value: any;
  api_source: string;
  field_path: string;
  is_array?: boolean;
  is_array_item?: boolean;
  array_index?: number;
  array_length?: number;
  is_truncated?: boolean;
  total_items?: number;
  remaining_items?: number;
  is_remaining_items?: boolean;
  /** True when template expected this API but it was not called for this search */
  missing_api?: boolean;
  /** When set, value is a row object; render as table row */
  item_columns?: ReportSectionFieldColumn[];
}

interface ReportSection {
  heading: string;
  order: number;
  collapsible?: boolean;
  fields: ReportSectionField[];
}

interface SearchReportData {
  search_id: number;
  parameter_type: string;
  parameter_type_id?: number | null;
  search_value: string;
  country_code?: string | null;
  status: string;
  total_cost: number;
  created_at: string;
  completed_at: string | null;
  results: SearchResult[];
  summary: {
    total_apis_called: number;
    successful_apis: number;
    failed_apis: number;
    total_processing_time: number;
  };
  template_applied?: boolean;
  template_name?: string | null;
  template_id?: number | null;
  template_fallback?: boolean;
  sections?: ReportSection[];
  ai_summary?: string | null;
  ai_summary_generated?: boolean;
  ai_summary_generated_at?: string | null;
  ai_summary_cost?: number;
}

interface SearchReportProps {
  report: SearchReportData;
  onClose?: () => void;
  onDownloadPDF?: () => void;
  onRefreshReport?: () => Promise<void>;
}

export const SearchReport: React.FC<SearchReportProps> = ({ report, onClose, onDownloadPDF, onRefreshReport }) => {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showEmptyFieldsForSection, setShowEmptyFieldsForSection] = useState<Set<string>>(new Set());
  const [expandedArrays, setExpandedArrays] = useState<Set<string>>(new Set());
  const [expandedArrayItems, setExpandedArrayItems] = useState<Map<string, Set<number>>>(new Map());
  const [arrayVisibleItems, setArrayVisibleItems] = useState<Map<string, number>>(new Map());
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [unmaskedFields, setUnmaskedFields] = useState<Set<string>>(new Set());
  const { showToast } = useToast();
  const { user } = useAuth();

  const toggleEmptyFieldsForSection = (sectionKey: string) => {
    setShowEmptyFieldsForSection((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const toggleUnmaskField = (fieldKey: string) => {
    setUnmaskedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) next.delete(fieldKey);
      else next.add(fieldKey);
      return next;
    });
  };

  const handleCopyValue = (val: string, label: string) => {
    if (!val || val === '—' || val === 'N/A') return;
    navigator.clipboard.writeText(val);
    showToast(`Copied ${label} to clipboard`, 'success');
  };

  const isSensitiveField = (name: string, val: any): boolean => {
    if (typeof val !== 'string' && typeof val !== 'number') return false;
    const lower = name.toLowerCase();
    return (
      lower.includes('password') ||
      lower.includes('secret') ||
      lower.includes('token') ||
      lower.includes('hash') ||
      lower.includes('credential') ||
      lower.includes('pin') ||
      lower.includes('aadhaar') ||
      lower.includes('pan card') ||
      lower.includes('pan number') ||
      lower.includes('ssn')
    );
  };

  const handleGenerateOrRefreshAI = async (isRefresh = false) => {
    try {
      setIsGeneratingAI(true);
      showToast(isRefresh ? 'Refreshing AI Intelligence Dossier...' : 'Synthesizing AI Intelligence Dossier...', 'info');
      if (isRefresh) {
        await apiService.refreshAIReport(report.search_id);
      } else {
        await apiService.generateAIReport(report.search_id);
      }
      showToast(isRefresh ? 'AI Dossier refreshed successfully' : 'AI Dossier generated successfully', 'success');
      if (onRefreshReport) {
        await onRefreshReport();
      }
    } catch (err: any) {
      console.error('Failed to generate/refresh AI dossier:', err);
      showToast(err.message || 'Failed to generate AI dossier', 'error');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const renderFormattedInlineText = (text: string) => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const urlMatch = remaining.match(/(https?:\/\/[^\s]+)/);

      let earliestMatch: { type: 'bold' | 'url'; index: number; length: number; match: RegExpMatchArray } | null = null;

      if (boldMatch && boldMatch.index !== undefined) {
        earliestMatch = { type: 'bold', index: boldMatch.index, length: boldMatch[0].length, match: boldMatch };
      }

      if (urlMatch && urlMatch.index !== undefined) {
        if (!earliestMatch || urlMatch.index < earliestMatch.index) {
          earliestMatch = { type: 'url', index: urlMatch.index, length: urlMatch[0].length, match: urlMatch };
        }
      }

      if (!earliestMatch) {
        parts.push(remaining);
        break;
      }

      if (earliestMatch.index > 0) {
        parts.push(remaining.slice(0, earliestMatch.index));
      }

      if (earliestMatch.type === 'bold') {
        parts.push(<strong key={key++} className="font-semibold text-slate-950 dark:text-white">{earliestMatch.match[1]}</strong>);
      } else if (earliestMatch.type === 'url') {
        const url = earliestMatch.match[1];
        parts.push(
          <a key={key++} href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline break-all">
            {url}
          </a>
        );
      }

      remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
    }

    return parts;
  };

  const renderMarkdownContent = (content: string) => {
    if (!content) return null;
    const paragraphs = content.split('\n\n');
    return (
      <div className="space-y-3.5 text-sm text-slate-800 dark:text-slate-200">
        {paragraphs.map((para, pIdx) => {
          const trimmed = para.trim();
          if (!trimmed) return null;

          if (trimmed.startsWith('## ')) {
            return (
              <h3 key={pIdx} className="text-base font-bold text-indigo-950 dark:text-indigo-200 border-b border-indigo-100 dark:border-indigo-900/50 pb-1.5 mt-5 first:mt-0 tracking-tight">
                {trimmed.replace(/^##\s+/, '')}
              </h3>
            );
          }

          if (trimmed.startsWith('### ')) {
            return (
              <h4 key={pIdx} className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mt-3">
                {trimmed.replace(/^###\s+/, '')}
              </h4>
            );
          }

          if (trimmed === '---') {
            return <hr key={pIdx} className="border-indigo-100 dark:border-indigo-900/40 my-3.5" />;
          }

          const lines = trimmed.split('\n');
          const isList = lines.every(l => /^\s*[-*•]\s+/.test(l));
          if (isList) {
            return (
              <ul key={pIdx} className="list-disc pl-5 space-y-1.5 my-1.5 text-xs sm:text-sm">
                {lines.map((line, lIdx) => {
                  const itemContent = line.replace(/^\s*[-*•]\s+/, '');
                  return (
                    <li key={lIdx} className="leading-relaxed">
                      {renderFormattedInlineText(itemContent)}
                    </li>
                  );
                })}
              </ul>
            );
          }

          return (
            <p key={pIdx} className="leading-relaxed text-xs sm:text-sm">
              {renderFormattedInlineText(trimmed)}
            </p>
          );
        })}
      </div>
    );
  };

  const isSuperuser = Boolean(user?.isSuperuser);

  const displaySections = useMemo((): ReportSection[] => {
    if (!report?.sections?.length) return [];
    return report.sections
      .map((section) => ({
        ...section,
        fields: (section.fields ?? []).filter(
          (f) => !isEmptyFieldValue(f.value, f.missing_api)
        ),
      }))
      .filter((section) => section.fields.length > 0);
  }, [report?.sections]);

  const reportFieldStats = useMemo(() => {
    if (!report?.sections?.length) return null;
    let total = 0;
    let filled = 0;
    for (const section of report.sections) {
      for (const field of section.fields ?? []) {
        total += 1;
        if (!isEmptyFieldValue(field.value, field.missing_api)) filled += 1;
      }
    }
    return {
      total,
      filled,
      completeness: total > 0 ? Math.round((filled / total) * 100) : 0,
    };
  }, [report?.sections]);

  const hasDisplayableData = displaySections.length > 0;

  // Format phone number with country code
  const formatPhoneNumber = (countryCode?: string | null, value?: string): string => {
    if (!countryCode || !value) return value || '';
    const clean = countryCode.startsWith('+') ? countryCode.slice(1) : countryCode;
    return `+${clean} ${value}`;
  };

  // Get formatted search value (with country code for phone numbers)
  const getFormattedSearchValue = (): string => {
    if (report.parameter_type === 'Phone Number' && report.country_code) {
      return formatPhoneNumber(report.country_code, report.search_value);
    }
    return report.search_value;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };


  // Function to flatten nested object structure
  const flattenObject = (obj: any, parentKey = '', separator = '_'): Record<string, any> => {
    const flattened: Record<string, any> = {};
    
    if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const newKey = parentKey ? `${parentKey}${separator}${key}` : key;
        
        if (Array.isArray(value)) {
          // Handle arrays - keep the array itself and also flatten each item if it's an object
          flattened[newKey] = value;
          
          if (value.length > 0) {
            value.forEach((item, index) => {
              if (typeof item === 'object' && item !== null) {
                // Flatten each array item with index
                Object.assign(flattened, flattenObject(item, `${newKey}_item_${index}`, separator));
              } else {
                // Simple array values
                flattened[`${newKey}_item_${index}`] = item;
              }
            });
            
            // Also create generic "_item" fields from first item for backward compatibility
            if (typeof value[0] === 'object' && value[0] !== null) {
              Object.assign(flattened, flattenObject(value[0], `${newKey}_item`, separator));
            }
          }
        } else if (typeof value === 'object' && value !== null) {
          // Recurse for nested objects
          Object.assign(flattened, flattenObject(value, newKey, separator));
        } else if (typeof value === 'string') {
          // Check if the string contains JSON-like structure
          const parsedValue = tryParseJsonString(value);
          if (parsedValue !== value) {
            // If it was parsed as JSON, flatten the parsed structure
            Object.assign(flattened, flattenObject(parsedValue, newKey, separator));
          } else {
            // This is a regular string
            flattened[newKey] = value;
          }
        } else {
          // This is a leaf value (number, boolean, null)
          flattened[newKey] = value;
        }
      }
    } else if (Array.isArray(obj)) {
      // Handle top-level arrays
      flattened[parentKey || 'items'] = obj;
      if (obj.length > 0) {
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            Object.assign(flattened, flattenObject(item, `${parentKey || 'item'}_${index}`, separator));
          } else {
            flattened[`${parentKey || 'item'}_${index}`] = item;
          }
        });
        
        // Also create generic "_item" fields from first item for backward compatibility
        if (typeof obj[0] === 'object' && obj[0] !== null) {
          Object.assign(flattened, flattenObject(obj[0], `${parentKey || 'item'}`, separator));
        }
      }
    } else {
      // Single value
      flattened[parentKey || 'value'] = obj;
    }
    
    return flattened;
  };

  // Function to try parsing a string as JSON
  const tryParseJsonString = (value: string): any => {
    if (typeof value !== 'string') {
      return value;
    }
    
    // Check if the string looks like JSON (starts with { or [ and ends with } or ])
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch (error) {
        // If parsing fails, return original value
        return value;
      }
    }
    
    return value;
  };

  // Extract key information from API response data (used only for text report fallback when no template)
  const extractKeyInfoForTextReport = (data: Record<string, any>, apiName: string): Record<string, any> => {
    // Check if data appears to have display parameters already applied
    // Display parameters typically have human-readable keys like "Found", "Quota", "Result 0 Email", etc.
    // vs technical keys like "data_found", "data_quota", "data_result_item_0_email"
    const hasDisplayParams = Object.keys(data).some(key => {
      // Display parameter keys are usually human-readable (contain spaces, title case, etc.)
      // or match patterns like "Result 0 Email", "Found", "Quota", etc.
      return /^[A-Z]/.test(key) || 
             /\s/.test(key) || 
             /^(Found|Quota|Success|Status Code|Search Results)/i.test(key) ||
             /^Result \d+/.test(key);
    });

    // If display parameters are already applied, use data as-is to preserve order
    if (hasDisplayParams) {
      const keyInfo: Record<string, any> = {};
      // Preserve order by iterating in the order received
      Object.entries(data).forEach(([key, value]) => {
        // Filter out internal/system fields but preserve everything else
        const excludeFields = ['_id', '__v', 'id', 'created_at', 'updated_at', 'timestamp', 'request_id', 'headers', 'status_code'];
        if (!excludeFields.includes(key) && value !== null && value !== undefined) {
          keyInfo[key] = value;
        }
      });
      return keyInfo;
    }

    // Otherwise, use the original extraction logic for backward compatibility
    const keyInfo: Record<string, any> = {};
    
    // Common fields that are usually important
    const commonFields = ['status', 'success', 'result', 'data', 'message', 'error', 'found', 'exists', 'quota'];
    
    // API-specific field mappings
    const apiSpecificFields: Record<string, string[]> = {
      'Account Finder': ['platforms_checked', 'total_platforms', 'accounts_found', 'successful_checks', 'failed_checks', 'found', 'message', 'total_users'],
      'leakcheck': ['found', 'sources', 'count', 'leaks'],
      'Google': ['results', 'total_results', 'items', 'queries'],
      'Web Search': ['results', 'total_results', 'items', 'queries'],
      'Email': ['valid', 'disposable', 'role', 'free_email', 'mx_record'],
      'Phone': ['valid', 'carrier', 'country', 'line_type', 'international_format']
    };

    // Determine which fields to extract based on API name
    let fieldsToExtract = commonFields;
    for (const [apiType, fields] of Object.entries(apiSpecificFields)) {
      if (apiName.toLowerCase().includes(apiType.toLowerCase())) {
        fieldsToExtract = [...fieldsToExtract, ...fields];
        break;
      }
    }

    // Extract key fields - preserve array structures
    fieldsToExtract.forEach(field => {
      if (data.hasOwnProperty(field)) {
        keyInfo[field] = data[field];
      }
    });

    // Special handling for data.result structure (common in many APIs)
    if (data.data && data.data.result && Array.isArray(data.data.result)) {
      keyInfo['result'] = data.data.result;
      // Also include other top-level fields from data.data
      if (data.data.found !== undefined) keyInfo['found'] = data.data.found;
      if (data.data.quota !== undefined) keyInfo['quota'] = data.data.quota;
      if (data.data.success !== undefined) keyInfo['success'] = data.data.success;
    } else if (data.result && Array.isArray(data.result)) {
      // Direct result array
      keyInfo['result'] = data.result;
    }

    // For Account Finder, extract platform results - support both formats
    if (apiName.toLowerCase().includes('account finder')) {
      let platformResults: Array<{ platform: string; status: string; account_url?: string; processing_time?: number }> = [];
      if (Array.isArray(data.platform_results)) {
        platformResults = data.platform_results.map((platform: any) => ({
          platform: platform.platform || platform.platform_name,
          status: platform.status || (platform.found ? 'YES' : 'NO'),
          account_url: platform.account_url,
          processing_time: platform.processing_time
        }));
      } else if (data.platforms && typeof data.platforms === 'object' && !Array.isArray(data.platforms)) {
        // Convert platforms object to array (backend may not have normalized yet)
        platformResults = Object.entries(data.platforms).map(([name, p]: [string, any]) => {
          const accountUrl = p?.account_url || (p?.users?.[0]?.url);
          return {
            platform: name,
            status: p?.found ? 'YES' : 'NO',
            account_url: accountUrl,
            processing_time: p?.processing_time
          };
        });
      } else if (data.data?.platforms && typeof data.data.platforms === 'object') {
        // Nested structure: data.data.platforms
        const platforms = data.data.platforms;
        platformResults = Object.entries(platforms).map(([name, p]: [string, any]) => ({
          platform: name,
          status: p?.found ? 'YES' : 'NO',
          account_url: p?.account_url || p?.users?.[0]?.url,
          processing_time: p?.processing_time
        }));
      }
      if (platformResults.length > 0) {
        keyInfo['platform_results'] = platformResults;
      }
    }

    // If no common fields found, show all data (this handles custom display parameters)
    // But filter out flattened keys that look like "result_item_0_email" - these should be handled as arrays
    if (Object.keys(keyInfo).length === 0) {
      // Filter out internal/system fields and flattened array keys
      const excludeFields = ['_id', '__v', 'id', 'created_at', 'updated_at', 'timestamp', 'request_id', 'headers', 'status_code'];
      const flattenedArrayPattern = /^(result|data|item).*_item_\d+/i;
      
      Object.entries(data).forEach(([key, value]) => {
        // Skip flattened array keys - these should be reconstructed from the original structure
        if (flattenedArrayPattern.test(key)) {
          return;
        }
        
        if (!excludeFields.includes(key) && value !== null && value !== undefined) {
          keyInfo[key] = value;
        }
      });
    }

    return keyInfo;
  };

  // Function to render array items in expandable format with pagination
  const renderArrayValue = (value: any[], fieldPath: string, displayName: string): React.ReactNode => {
    const arrayKey = `${fieldPath}_array`;
    const isExpanded = expandedArrays.has(arrayKey);
    const itemExpandedSet = expandedArrayItems.get(arrayKey) || new Set<number>();
    const visibleItems = arrayVisibleItems.get(arrayKey) || 50;
    const ITEMS_PER_PAGE = 50;

    if (value.length === 0) {
      return <span className="text-gray-400 italic">Empty array</span>;
    }

    // Simple array (strings, numbers, booleans)
    if (value.every(item => typeof item !== 'object' || item === null)) {
      const itemsToShow = isExpanded ? value.slice(0, visibleItems) : value.slice(0, 10);
      const hasMore = value.length > visibleItems;
      const hasMoreInitial = value.length > 10 && !isExpanded;

      return (
        <div className="space-y-1">
          {itemsToShow.map((item, idx) => (
            <div key={idx} className="text-sm bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
              {String(item)}
            </div>
          ))}
          
          {hasMoreInitial && !isExpanded && (
            <button
              onClick={() => {
                const newSet = new Set(expandedArrays);
                newSet.add(arrayKey);
                setExpandedArrays(newSet);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
            >
              Show all {value.length} items ({value.length - 10} more)
            </button>
          )}
          
          {isExpanded && hasMore && (
            <button
              onClick={() => {
                const newMap = new Map(arrayVisibleItems);
                const current = newMap.get(arrayKey) || 50;
                newMap.set(arrayKey, Math.min(current + ITEMS_PER_PAGE, value.length));
                setArrayVisibleItems(newMap);
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
            >
              Load more ({Math.min(ITEMS_PER_PAGE, value.length - visibleItems)} more of {value.length - visibleItems} remaining)
            </button>
          )}
          
          {isExpanded && !hasMore && value.length > 10 && (
            <button
              onClick={() => {
                const newSet = new Set(expandedArrays);
                newSet.delete(arrayKey);
                setExpandedArrays(newSet);
                const newMap = new Map(arrayVisibleItems);
                newMap.set(arrayKey, 50);
                setArrayVisibleItems(newMap);
              }}
              className="text-xs text-gray-600 dark:text-gray-400 hover:underline mt-1"
            >
              Show less
            </button>
          )}
        </div>
      );
    }

    // Complex array (objects) - with pagination for large arrays
    const itemsToShow = isExpanded ? value.slice(0, visibleItems) : value.slice(0, 10);
    const hasMore = value.length > visibleItems;
    const hasMoreInitial = value.length > 10 && !isExpanded;
    const totalItems = value.length;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {totalItems} item{totalItems !== 1 ? 's' : ''}
            {isExpanded && visibleItems < totalItems && (
              <span className="text-gray-500 ml-2">
                (showing {visibleItems} of {totalItems})
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {hasMoreInitial && !isExpanded && (
              <button
                onClick={() => {
                  const newSet = new Set(expandedArrays);
                  newSet.add(arrayKey);
                  setExpandedArrays(newSet);
                  const newMap = new Map(arrayVisibleItems);
                  newMap.set(arrayKey, 50);
                  setArrayVisibleItems(newMap);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Show all {totalItems} items
              </button>
            )}
            {isExpanded && (
              <button
                onClick={() => {
                  const newSet = new Set(expandedArrays);
                  newSet.delete(arrayKey);
                  const newMap = new Map(expandedArrayItems);
                  newMap.delete(arrayKey);
                  setExpandedArrayItems(newMap);
                  setExpandedArrays(newSet);
                  const visibleMap = new Map(arrayVisibleItems);
                  visibleMap.set(arrayKey, 50);
                  setArrayVisibleItems(visibleMap);
                }}
                className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
              >
                Collapse
              </button>
            )}
          </div>
        </div>
        
        {itemsToShow.map((item, idx) => {
          const actualIndex = isExpanded ? idx : idx;
          const isItemExpanded = itemExpandedSet.has(actualIndex);
          return (
            <div key={actualIndex} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => {
                  const newMap = new Map(expandedArrayItems);
                  const currentSet = newMap.get(arrayKey) || new Set<number>();
                  const newSet = new Set(currentSet);
                  if (isItemExpanded) {
                    newSet.delete(actualIndex);
                  } else {
                    newSet.add(actualIndex);
                  }
                  newMap.set(arrayKey, newSet);
                  setExpandedArrayItems(newMap);
                }}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Item {actualIndex + 1}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${isItemExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isItemExpanded && (
                <div className="p-3 bg-white dark:bg-gray-900">
                  {typeof item === 'object' && item !== null ? (
                    <div className="space-y-2">
                      {Object.entries(item).map(([key, val]) => (
                        <div key={key} className="flex items-start gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[120px]">
                            {key}:
                          </span>
                          <span className="text-sm text-gray-800 dark:text-gray-200 flex-1">
                            {formatFieldValue(val, undefined)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm">{String(item)}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        
        {isExpanded && hasMore && (
          <div className="flex justify-center pt-2">
            <button
              onClick={() => {
                const newMap = new Map(arrayVisibleItems);
                const current = newMap.get(arrayKey) || 50;
                newMap.set(arrayKey, Math.min(current + ITEMS_PER_PAGE, totalItems));
                setArrayVisibleItems(newMap);
              }}
              className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Load More ({Math.min(ITEMS_PER_PAGE, totalItems - visibleItems)} more of {totalItems - visibleItems} remaining)
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatFieldValue = (value: any, field?: ReportSectionField): string | React.ReactNode => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'number') {
      return value.toString();
    }
    
    if (typeof value === 'string') {
      const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
      const isImageUrl = (v: string) =>
        (v.startsWith('http://') || v.startsWith('https://')) && imageExtensions.test(v);
      const isDataImage = (v: string) => v.startsWith('data:image/');

      // Base64 or data URL image
      if (isDataImage(value)) {
        return (
          <div className="mt-1">
            <img
              src={value}
              alt=""
              className="max-w-full max-h-48 object-contain rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
              onClick={() => window.open(value, '_blank')}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
              }}
            />
            <a href={value} target="_blank" rel="noopener noreferrer" className="hidden text-xs text-gray-500 break-all">
              {value.slice(0, 60)}...
            </a>
          </div>
        );
      }
      // Image URL (http/https with image extension)
      if (isImageUrl(value)) {
        return (
          <div className="mt-1">
            <img
              src={value}
              alt=""
              className="max-w-full max-h-48 object-contain rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
              onClick={() => window.open(value, '_blank')}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
              }}
            />
            <a href={value} target="_blank" rel="noopener noreferrer" className="hidden text-blue-600 dark:text-blue-400 text-xs break-all">
              {value}
            </a>
          </div>
        );
      }
      // Plain URL (non-image)
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline break-all whitespace-pre-wrap"
          >
            {value}
          </a>
        );
      }
      // Show full text; wrap row by row
      return <span className="whitespace-pre-wrap break-words">{value}</span>;
    }
    
    if (Array.isArray(value)) {
      return renderArrayValue(value, field?.field_path || 'array', field?.display_name || 'Array');
    }
    
    if (typeof value === 'object') {
      // For objects in template view, show key-value pairs
      return (
        <div className="space-y-1 mt-1">
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="text-sm bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
              <span className="font-medium text-gray-700 dark:text-gray-300">{key}:</span>{' '}
              <span className="text-gray-600 dark:text-gray-400">{formatFieldValue(val)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    return String(value);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (typeof value === 'string') {
      return value.length > 100 ? value.substring(0, 100) + '...' : value;
    }
    if (Array.isArray(value)) {
      return `${value.length} items`;
    }
    if (typeof value === 'object') {
      return `${Object.keys(value).length} fields`;
    }
    return String(value);
  };


  const handleDownloadReport = async () => {
    try {
      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // Helper function to add text with word wrapping
      const addText = (text: string, x: number, y: number, options: any = {}) => {
        const fontSize = options.fontSize || 10;
        const fontStyle = options.fontStyle || 'normal';
        const maxWidth = options.maxWidth || contentWidth;
        
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return y + (lines.length * fontSize * 0.5);
      };
      
      // Helper function to check if new page is needed
      const checkNewPage = (requiredSpace: number = 20) => {
        if (yPosition + requiredSpace > doc.internal.pageSize.height - 20) {
          doc.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to format field value for PDF
      const formatFieldValueForPDF = (value: any): string => {
        if (value === null || value === undefined) {
          return 'N/A';
        }
        
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        
        if (typeof value === 'number') {
          return value.toString();
        }
        
        if (typeof value === 'string') {
          // Truncate long strings
          return value.length > 200 ? value.substring(0, 200) + '...' : value;
        }
        
        if (Array.isArray(value)) {
          if (value.length === 0) return 'None';
          // Show first few items for arrays
          const preview = value.slice(0, 5).map(item => 
            typeof item === 'object' ? JSON.stringify(item).substring(0, 100) : String(item)
          ).join(', ');
          return value.length > 5 ? `${preview}... (+${value.length - 5} more)` : preview;
        }
        
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2).substring(0, 200) + (JSON.stringify(value).length > 200 ? '...' : '');
        }
        
        return String(value);
      };
      
      // Title
      yPosition = addText('OSINT Search Report', margin, yPosition, { fontSize: 20, fontStyle: 'bold' });
      yPosition += 10;
      
      // AI Summary section at the top (if available)
      if (report.ai_summary_generated && report.ai_summary) {
        checkNewPage(30);
        yPosition += 10;
        yPosition = addText('AI-Generated Summary', margin, yPosition, { fontSize: 14, fontStyle: 'bold' }) + 3;
        yPosition = addText('='.repeat(50), margin, yPosition) + 5;
        
        // Add AI summary text with word wrapping
        const summaryLines = doc.splitTextToSize(report.ai_summary, contentWidth);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        summaryLines.forEach((line: string) => {
          checkNewPage();
          yPosition = addText(line, margin, yPosition) + 4;
        });
        
        yPosition += 5;
        if (report.ai_summary_cost !== undefined && report.ai_summary_cost > 0) {
          yPosition = addText(`AI Summary Cost: ${report.ai_summary_cost} Int Coins`, margin, yPosition) + 3;
        }
        if (report.ai_summary_generated_at) {
          yPosition = addText(`Generated: ${formatDateTime(report.ai_summary_generated_at)}`, margin, yPosition) + 5;
        }
        yPosition += 10;
      }
      
      // Search Details Section
      checkNewPage(30);
      yPosition = addText('Search Information', margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
      yPosition += 5;
      
      const searchDetails = [
        `Search ID: ${report.search_id}`,
        `Parameter Type: ${report.parameter_type}`,
        `Search Value: ${getFormattedSearchValue()}`,
        `Status: ${report.status}`,
        `Total Cost: ${report.total_cost} Int Coins`,
        `Created: ${formatDateTime(report.created_at)}`,
        ...(report.completed_at ? [`Completed: ${formatDateTime(report.completed_at)}`] : [])
      ];
      
      searchDetails.forEach(detail => {
        checkNewPage();
        yPosition = addText(detail, margin, yPosition) + 3;
      });
      
      yPosition += 10;
      
      // Template View (if available)
      const hasTemplate = report.template_applied && report.sections && report.sections.length > 0;
      
      if (hasTemplate) {
        checkNewPage(30);
        
        // Template header
        if (report.template_name) {
          yPosition = addText(`Template: ${report.template_name}`, margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
          yPosition += 5;
          yPosition = addText('Report organized using the template structure', margin, yPosition, { fontSize: 10 }) + 10;
        }
        
        // Sort sections by order (copy so we don't mutate report)
        const sortedSections = [...(report.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        
        // Render each section
        sortedSections.forEach((section, sectionIndex) => {
          checkNewPage(30);
          
          // Section heading
          yPosition = addText(section.heading || `Section ${sectionIndex + 1}`, margin, yPosition, { fontSize: 12, fontStyle: 'bold' });
          yPosition += 5;
          
          if (section.fields && section.fields.length > 0) {
            section.fields.forEach((field: ReportSectionField, fieldIndex: number) => {
              checkNewPage(15);
              
              const displayName = field.display_name;
              const formattedValue = formatFieldValueForPDF(field.value);
              
              // Format field display
              yPosition = addText(`${displayName}:`, margin + 10, yPosition, { fontSize: 11, fontStyle: 'bold' });
              yPosition += 3;
              
              // Add value with word wrapping
              const valueLines = doc.splitTextToSize(formattedValue, contentWidth - 20);
              valueLines.forEach((line: string) => {
                checkNewPage();
                yPosition = addText(line, margin + 20, yPosition) + 3;
              });
              
              yPosition += 3;
            });
          } else {
            checkNewPage();
            yPosition = addText('No fields available in this section', margin + 10, yPosition, { fontSize: 10 }) + 5;
          }
          
          yPosition += 5;
        });
      } else {
        // API Results Section (only if no template view)
        checkNewPage(30);
        yPosition = addText('API Results', margin, yPosition, { fontSize: 14, fontStyle: 'bold' });
        yPosition += 10;
        
        // Successful APIs first
        const successfulResults = report.results.filter(result => result.status === 'success');
        const failedResults = report.results.filter(result => result.status !== 'success');
        
        successfulResults.forEach((result, index) => {
          checkNewPage(40);
          
          // API Header
          yPosition = addText(`${index + 1}. ${result.api_name}`, margin, yPosition, { fontSize: 12, fontStyle: 'bold' });
          yPosition += 3;
          yPosition = addText(`Status: ${result.status}`, margin + 10, yPosition) + 2;
          if (result.processing_time) {
            yPosition = addText(`Processing Time: ${result.processing_time.toFixed(2)}s`, margin + 10, yPosition) + 5;
          }
          
          // Extract and display data
          if (result.data) {
            const keyInfo = extractKeyInfoForTextReport(result.data, result.api_name);
            
            if (Object.keys(keyInfo).length > 0) {
              yPosition = addText('Data:', margin + 10, yPosition, { fontSize: 11, fontStyle: 'bold' });
              yPosition += 3;
              
              Object.entries(keyInfo).forEach(([key, value]) => {
                if (key !== 'platform_results') {
                  checkNewPage();
                  const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  let displayValue = '';
                  
                  // Format value based on type (matching frontend display)
                  if (value === null || value === undefined) {
                    displayValue = 'N/A';
                  } else if (typeof value === 'boolean') {
                    displayValue = value ? 'Yes' : 'No';
                  } else if (Array.isArray(value)) {
                    if (value.length === 0) {
                      displayValue = 'Empty';
                    } else if (value.every(item => typeof item === 'string' || typeof item === 'number')) {
                      displayValue = value.join(', ');
                    } else {
                      displayValue = `${value.length} items`;
                    }
                  } else if (typeof value === 'object') {
                    displayValue = `${Object.keys(value).length} fields`;
                  } else {
                    displayValue = String(value);
                  }
                  
                  yPosition = addText(`${displayKey}: ${displayValue}`, margin + 20, yPosition) + 2;
                }
              });
              
              // Platform results
              if (keyInfo.platform_results && Array.isArray(keyInfo.platform_results)) {
                checkNewPage(20);
                yPosition = addText('Platform Results:', margin + 10, yPosition, { fontSize: 11, fontStyle: 'bold' });
                yPosition += 3;
                
                keyInfo.platform_results.forEach((platform: any, pIndex: number) => {
                  checkNewPage();
                  yPosition = addText(`${pIndex + 1}. ${platform.platform || 'Unknown Platform'}`, margin + 20, yPosition, { fontStyle: 'bold' }) + 2;
                  
                  if (platform.username) {
                    yPosition = addText(`Username: ${platform.username}`, margin + 30, yPosition) + 2;
                  }
                  if (platform.url) {
                    yPosition = addText(`URL: ${platform.url}`, margin + 30, yPosition, { maxWidth: contentWidth - 40 }) + 2;
                  }
                  if (platform.exists !== undefined) {
                    yPosition = addText(`Exists: ${platform.exists ? 'Yes' : 'No'}`, margin + 30, yPosition) + 2;
                  }
                  yPosition += 3;
                });
              }
            } else {
              yPosition = addText('No structured data available', margin + 20, yPosition, { fontSize: 10 }) + 5;
            }
          }
          
          yPosition += 10;
        });
        
        // Failed APIs
        if (failedResults.length > 0) {
          checkNewPage(30);
          yPosition = addText('Failed API Requests', margin, yPosition, { fontSize: 12, fontStyle: 'bold' });
          yPosition += 10;
          
          failedResults.forEach((result, index) => {
            checkNewPage(20);
            yPosition = addText(`${index + 1}. ${result.api_name}`, margin + 10, yPosition, { fontSize: 11, fontStyle: 'bold' });
            yPosition += 3;
            yPosition = addText(`Status: ${result.status}`, margin + 20, yPosition) + 2;
            yPosition = addText(`Error: ${result.error_message || 'Unknown error occurred'}`, margin + 20, yPosition, { maxWidth: contentWidth - 40 }) + 5;
          });
        }
      }
      
      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on ${formatDateTime(new Date().toISOString())} | Page ${i} of ${pageCount}`, margin, doc.internal.pageSize.height - 10);
      }
      
      // Save the PDF
      const fileName = `osint-report-${report.search_id}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to text download
      const reportText = generateTextReport(report);
      const blob = new Blob([reportText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `osint-report-${report.search_id}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateTextReport = (report: SearchReportData): string => {
    let reportText = `OSINT Search Report\n`;
    reportText += `================================\n\n`;
    reportText += `Search ID: ${report.search_id}\n`;
    reportText += `Parameter Type: ${report.parameter_type}\n`;
    reportText += `Search Value: ${getFormattedSearchValue()}\n`;
    reportText += `Status: ${report.status}\n`;
    reportText += `Total Cost: ${report.total_cost} Int Coins\n`;
    reportText += `Created: ${formatDateTime(report.created_at)}\n`;
    if (report.completed_at) {
      reportText += `Completed: ${formatDateTime(report.completed_at)}\n`;
    }
    // Add AI Summary if available
    if (report.ai_summary_generated && report.ai_summary) {
      reportText += `\nAI-Generated Summary:\n`;
      reportText += `====================\n\n`;
      reportText += `${report.ai_summary}\n\n`;
      if (report.ai_summary_cost !== undefined && report.ai_summary_cost > 0) {
        reportText += `AI Summary Cost: ${report.ai_summary_cost} Int Coins\n`;
      }
      if (report.ai_summary_generated_at) {
        reportText += `Generated: ${formatDateTime(report.ai_summary_generated_at)}\n`;
      }
      reportText += `\n`;
    }
    
    // Template sections (template-only)
    if (report.sections && report.sections.length > 0) {
      const sortedSections = [...report.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      sortedSections.forEach((section) => {
        reportText += `\n${section.heading || 'Section'}\n`;
        reportText += `${'-'.repeat((section.heading || 'Section').length)}\n\n`;
        (section.fields || []).forEach((field) => {
          reportText += `  ${field.display_name}: ${formatValue(field.value)}\n`;
        });
        reportText += `\n`;
      });
    } else {
      reportText += `\nOSINT Results:\n`;
      reportText += `============\n\n`;
      report.results.forEach((result, index) => {
        reportText += `${index + 1}. ${result.api_name}\n`;
        reportText += `   Status: ${result.status}\n`;
        reportText += `   Processing Time: ${result.processing_time}s\n`;
        if (result.status === 'success' && Object.keys(result.data).length > 0) {
          const keyInfo = extractKeyInfoForTextReport(result.data, result.api_name);
          reportText += `   Key Information:\n`;
          Object.entries(keyInfo).forEach(([key, value]) => {
            reportText += `     ${key}: ${formatValue(value)}\n`;
          });
        } else if (result.status === 'failed') {
          reportText += `   Error: ${result.error_message || 'Unknown error'}\n`;
        }
        reportText += `\n`;
      });
    }
    return reportText;
  };

  // Defensive programming - handle incomplete data
  if (!report) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Search Report</h2>
          {onClose && (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
        <Card>
          <div className="p-6 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500">No report data available.</p>
            <p className="text-sm text-gray-400 mt-2">The search report could not be loaded or doesn't exist.</p>
          </div>
        </Card>
      </div>
    );
  }

  // Ensure results exists
  const results = report.results || [];

  return (
    <div className="space-y-4 text-left">
      {/* Executive Intelligence Header with KPI Chips */}
      <Card className="overflow-hidden border border-slate-200 dark:border-slate-700/80 shadow-sm bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-slate-900 dark:via-slate-800/90 dark:to-slate-900">
        <div className="p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 dark:border-slate-700/50 pb-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-300">
                  {report.parameter_type}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  ID: #{report.search_id}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  report.status === 'completed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    report.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}></span>
                  {report.status ? report.status.toUpperCase() : 'COMPLETED'}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white break-words tracking-tight">
                {getFormattedSearchValue()}
              </h1>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={onDownloadPDF ? onDownloadPDF : handleDownloadReport}
                className="text-xs px-3 py-1.5 shadow-sm"
              >
                <svg className="w-4 h-4 mr-1.5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export PDF
              </Button>
              {isSuperuser && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                  className="text-xs px-3 py-1.5"
                >
                  <svg className={`w-3.5 h-3.5 mr-1.5 transition-transform ${showTechnicalDetails ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {showTechnicalDetails ? 'Hide Debug' : 'Debug'}
                </Button>
              )}
              {onClose && (
                <Button variant="secondary" size="sm" onClick={onClose} className="text-xs px-3 py-1.5">
                  Close
                </Button>
              )}
            </div>
          </div>

          {/* Metric KPI Chips */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 text-xs">
            <div className="bg-white dark:bg-slate-800/80 rounded-lg p-2.5 border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
              <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Sources Queried</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                {report.summary?.successful_apis || 0} / {report.summary?.total_apis_called || 0} ok
              </span>
            </div>
            <div className="bg-white dark:bg-slate-800/80 rounded-lg p-2.5 border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
              <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Duration</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                {report.summary?.total_processing_time != null ? `${report.summary.total_processing_time}s` : '—'}
              </span>
            </div>
            <div className="bg-white dark:bg-slate-800/80 rounded-lg p-2.5 border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
              <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Total Cost</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                {report.total_cost ?? 0} Int Coins
              </span>
            </div>
            <div className="bg-white dark:bg-slate-800/80 rounded-lg p-2.5 border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
              <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Data Completeness</span>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400 text-sm">
                {reportFieldStats ? `${reportFieldStats.completeness}%` : '—'}
              </span>
            </div>
            <div className="bg-white dark:bg-slate-800/80 rounded-lg p-2.5 border border-slate-200/80 dark:border-slate-700/60 shadow-xs col-span-2 sm:col-span-1">
              <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Queried At</span>
              <span className="font-medium text-slate-700 dark:text-slate-300 text-xs truncate block" title={formatDateTime(report.created_at)}>
                {formatDateTime(report.created_at)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Jump Navigation Bar (TOC) */}
      <div className="sticky top-2 z-10 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-xl p-2 border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-1.5 overflow-x-auto text-xs no-scrollbar">
        <span className="text-slate-400 text-[11px] font-semibold uppercase px-2 shrink-0">Jump To:</span>
        <button
          type="button"
          onClick={() => document.getElementById('ai-summary-section')?.scrollIntoView({ behavior: 'smooth' })}
          className="px-2.5 py-1 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/60 dark:text-indigo-300 font-medium shrink-0 transition-colors"
        >
          🤖 AI Dossier
        </button>
        {displaySections.map((sec, idx) => {
          const friendly = getFriendlySectionTitle(sec.heading || `Section ${idx + 1}`);
          return (
            <button
              key={idx}
              type="button"
              onClick={() => document.getElementById(`section-${idx}`)?.scrollIntoView({ behavior: 'smooth' })}
              className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:hover:bg-slate-700 dark:text-slate-300 shrink-0 transition-colors"
            >
              {friendly}
            </button>
          );
        })}
        {isSuperuser && (
          <button
            type="button"
            onClick={() => {
              setShowTechnicalDetails(true);
              setTimeout(() => document.getElementById('technical-details-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
            }}
            className="px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 dark:text-amber-300 shrink-0 transition-colors"
          >
            ⚙️ Debug Logs
          </button>
        )}
      </div>

      {/* AI Intelligence Dossier Section (Available to All Users) */}
      <div id="ai-summary-section">
        {report.ai_summary_generated && report.ai_summary ? (
          <Card className="border border-indigo-200 dark:border-indigo-900/60 shadow-md bg-gradient-to-br from-indigo-50/60 via-white to-blue-50/40 dark:from-indigo-950/30 dark:via-slate-900 dark:to-slate-900">
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 border-b border-indigo-100 dark:border-indigo-900/40 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-sm">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-bold text-slate-900 dark:text-white">AI Intelligence Dossier</h2>
                      <Badge variant="default" className="bg-indigo-600 text-white text-[10px] px-2 py-0.5">
                        Synthesized
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {report.ai_summary_generated_at && (
                        <span>Generated: {formatDateTime(report.ai_summary_generated_at)}</span>
                      )}
                      {report.ai_summary_cost !== undefined && report.ai_summary_cost > 0 && (
                        <span>• Cost: {report.ai_summary_cost} Int Coins</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateOrRefreshAI(true)}
                  disabled={isGeneratingAI}
                  className="text-xs px-3 py-1.5 shrink-0 self-start sm:self-auto border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                >
                  {isGeneratingAI ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-1.5"></div>
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh Analysis
                    </>
                  )}
                </Button>
              </div>

              {/* Rich Markdown Rendered AI Summary */}
              <div className="bg-white/80 dark:bg-slate-800/80 rounded-xl p-5 border border-indigo-100/80 dark:border-indigo-900/40 shadow-xs">
                {renderMarkdownContent(report.ai_summary)}
              </div>
            </div>
          </Card>
        ) : (
          <Card className="border border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-r from-indigo-50/40 to-blue-50/30 dark:from-indigo-950/20 dark:to-slate-900/60 p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3.5 text-left">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 rounded-xl shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    Generate AI Intelligence Dossier
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Synthesize, cross-correlate, and extract actionable findings from all {report.summary?.successful_apis || 0} successful sources into a single dossier.
                  </p>
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleGenerateOrRefreshAI(false)}
                disabled={isGeneratingAI}
                className="text-xs px-4 py-2 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
              >
                {isGeneratingAI ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-2"></div>
                    Synthesizing Dossier...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Generate AI Dossier
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Superadmin: technical / debug panel */}
      {isSuperuser && showTechnicalDetails && (
        <Card id="technical-details-section" className="border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <div className="p-4 space-y-4 text-left">
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100 uppercase tracking-wide">
              Technical details (superadmin)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-100 dark:border-amber-900/40">
                <span className="text-gray-500 dark:text-gray-400 text-xs block">Search ID</span>
                <span className="font-mono text-gray-900 dark:text-white">{report.search_id}</span>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-100 dark:border-amber-900/40">
                <span className="text-gray-500 dark:text-gray-400 text-xs block">Status</span>
                <span className="text-gray-900 dark:text-white">{report.status ?? '—'}</span>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-100 dark:border-amber-900/40">
                <span className="text-gray-500 dark:text-gray-400 text-xs block">Cost</span>
                <span className="text-gray-900 dark:text-white">{report.total_cost ?? '—'} Int Coins</span>
              </div>
              {report.summary && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-100 dark:border-amber-900/40">
                  <span className="text-gray-500 dark:text-gray-400 text-xs block">APIs</span>
                  <span className="text-gray-900 dark:text-white">
                    {report.summary.successful_apis}/{report.summary.total_apis_called} ok
                    {(report.summary.failed_apis ?? 0) > 0 && `, ${report.summary.failed_apis} failed`}
                  </span>
                </div>
              )}
            </div>
            {reportFieldStats && (
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Template fill: {reportFieldStats.completeness}% ({reportFieldStats.filled}/{reportFieldStats.total} fields)
                {report.template_name && <> · Template: {report.template_name}</>}
              </p>
            )}
            {results.length > 0 && (
              <div className="space-y-4">
                {results.map((result, idx) => (
                  <div
                    key={result.api_id ?? idx}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{result.api_name}</span>
                      <div className="flex items-center gap-2">
                        {result.response_status != null && (
                          <span className="text-xs text-gray-500">HTTP {result.response_status}</span>
                        )}
                        <Badge variant={result.status === 'success' ? 'success' : 'error'} size="sm">
                          {result.status}
                        </Badge>
                        {result.processing_time != null && (
                          <span className="text-xs text-gray-500">{result.processing_time}s</span>
                        )}
                      </div>
                    </div>
                    <div className="p-3 space-y-3 text-xs">
                      {result.status !== 'success' && (
                        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2 py-1.5 text-red-800 dark:text-red-200">
                          {result.error_message?.trim() || 'Request failed or returned no usable data'}
                        </div>
                      )}
                      {result.request_url && (
                        <div>
                          <p className="font-medium text-gray-600 dark:text-gray-300 mb-1">Request URL</p>
                          <pre className="bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto text-[11px]">{result.request_url}</pre>
                        </div>
                      )}
                      {result.request_body != null && Object.keys(result.request_body).length > 0 && (
                        <div>
                          <p className="font-medium text-gray-600 dark:text-gray-300 mb-1">Request body</p>
                          <pre className="bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto text-[11px]">
                            {JSON.stringify(result.request_body, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-600 dark:text-gray-300 mb-1">Response</p>
                        <pre className="bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto text-[11px] max-h-64 overflow-y-auto">
                          {JSON.stringify(result.raw_response ?? result.data ?? {}, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Data sections — received values only */}
      {hasDisplayableData && (
        <div className="space-y-6 text-left">
          {[...displaySections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((section, sectionIndex) => {
              const sectionKey = section.heading || `section-${sectionIndex}`;
              const friendlyTitle = getFriendlySectionTitle(section.heading || `Section ${sectionIndex + 1}`);
              const showEmpty = isSuperuser && showTechnicalDetails && showEmptyFieldsForSection.has(sectionKey);

              return (
              <Card key={sectionIndex} id={`section-${sectionIndex}`} className="overflow-hidden text-left scroll-mt-14">
                <div className="p-3 text-left">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-left">
                    {friendlyTitle}
                  </h3>
                  {section.fields && section.fields.length > 0 ? (
                    <div className="space-y-3">
                      {(() => {
                        const orderedBlocks: (ReportSectionField | ReportSectionField[])[] = [];
                        let i = 0;
                        while (i < section.fields.length) {
                          const field = section.fields[i];
                          if (field.is_array_item && field.array_index !== undefined) {
                            const baseName = field.display_name.replace(/\s*\[\d+\]\s*$/, '');
                            const group: ReportSectionField[] = [field];
                            i++;
                            while (i < section.fields.length) {
                              const next = section.fields[i];
                              if (next.is_array_item && next.array_index !== undefined) {
                                const nextBase = next.display_name.replace(/\s*\[\d+\]\s*$/, '');
                                if (nextBase === baseName) {
                                  group.push(next);
                                  i++;
                                } else break;
                              } else break;
                            }
                            group.sort((a, b) => (a.array_index ?? 0) - (b.array_index ?? 0));
                            orderedBlocks.push(group);
                          } else {
                            orderedBlocks.push(field);
                            i++;
                          }
                        }
                        const simpleFields = orderedBlocks.filter((b): b is ReportSectionField => !Array.isArray(b)) as ReportSectionField[];
                        const arrayBlocks = orderedBlocks.filter((b): b is ReportSectionField[] => Array.isArray(b));
                        const withData = simpleFields.filter((f) => !isEmptyFieldValue(f.value, f.missing_api));
                        const emptyCount = simpleFields.length - withData.length;
                        const visibleSimpleFields = showEmpty ? simpleFields : withData;

                        return (
                          <>
                            {visibleSimpleFields.length > 0 && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-left">
                                {visibleSimpleFields.map((field, blockIndex) => {
                                  const formattedValue = formatFieldValue(field.value, field);
                                  const fieldKey = `${sectionKey}-${field.display_name}-${blockIndex}`;
                                  const sensitive = isSensitiveField(field.display_name, formattedValue);
                                  const isUnmasked = unmaskedFields.has(fieldKey);
                                  const displayVal = sensitive && !isUnmasked ? '••••••••••••' : formattedValue;
                                  const rawValStr = typeof field.value === 'object' ? JSON.stringify(field.value) : String(formattedValue || '');

                                  return (
                                    <div
                                      key={`field-${blockIndex}`}
                                      className="group flex items-start justify-between gap-2 py-1.5 border-b border-gray-100 dark:border-gray-700/50 min-w-0 text-left hover:bg-slate-50/60 dark:hover:bg-slate-800/40 px-1.5 rounded transition-colors"
                                    >
                                      <div className="min-w-0 flex-1 flex gap-2">
                                        <span className="text-gray-500 dark:text-gray-400 shrink-0 break-words max-w-[45%] text-xs sm:text-sm" title={field.display_name}>
                                          {field.display_name}
                                        </span>
                                        <span className="text-gray-900 dark:text-white break-words whitespace-pre-wrap min-w-0 flex-1 text-xs sm:text-sm font-medium">
                                          {displayVal}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        {sensitive && (
                                          <button
                                            type="button"
                                            onClick={() => toggleUnmaskField(fieldKey)}
                                            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
                                            title={isUnmasked ? "Mask value" : "Reveal value"}
                                          >
                                            {isUnmasked ? (
                                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                              </svg>
                                            ) : (
                                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                              </svg>
                                            )}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => handleCopyValue(rawValStr, field.display_name)}
                                          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded transition-colors"
                                          title="Copy value"
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {isSuperuser && showTechnicalDetails && !showEmpty && emptyCount > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleEmptyFieldsForSection(sectionKey)}
                                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline mt-1"
                              >
                                Show {emptyCount} empty field{emptyCount !== 1 ? 's' : ''}
                              </button>
                            )}
                            {isSuperuser && showTechnicalDetails && showEmpty && emptyCount > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleEmptyFieldsForSection(sectionKey)}
                                className="text-xs text-gray-500 dark:text-gray-400 hover:underline mt-1"
                              >
                                Hide empty fields
                              </button>
                            )}
                            {isSuperuser && showTechnicalDetails && visibleSimpleFields.length === 0 && arrayBlocks.length === 0 && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                No data returned for this section.
                              </p>
                            )}
                            {arrayBlocks.map((fields, blockIndex) => {
                              const baseName = fields[0]?.display_name.replace(/\s*\[\d+\]\s*$/, '') ?? '';
                              const columns = fields[0]?.item_columns;
                              const asTable = columns && columns.length > 0 && typeof fields[0]?.value === 'object' && fields[0]?.value !== null && !Array.isArray(fields[0]?.value);
                              return (
                                <div
                                  key={`array-group-${blockIndex}`}
                                  className="border-l-4 border-blue-500 pl-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-r mt-3 text-left"
                                >
                                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1.5 text-left">
                                    {baseName} ({fields.length} items)
                                  </div>
                                  {asTable ? (
                                    <div className="ml-2 overflow-x-auto rounded border border-gray-200 dark:border-gray-600">
                                      <table className="min-w-full text-xs text-left">
                                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                          <tr>
                                            <th className="px-2 py-1.5 font-medium w-8">#</th>
                                            {columns.map((col, cIdx) => (
                                              <th key={cIdx} className="px-2 py-1.5 font-medium">{col.display_name}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800">
                                          {fields.filter((f) => !f.is_remaining_items).map((field, fieldIndex) => (
                                            <tr key={fieldIndex} className="border-t border-gray-200 dark:border-gray-600">
                                              <td className="px-2 py-1.5 text-gray-500">{(field.array_index ?? 0) + 1}</td>
                                              {columns.map((col, cIdx) => (
                                                <td key={cIdx} className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                                                  {formatFieldValue((field.value as Record<string, unknown>)?.[col.path], field)}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="space-y-1 ml-2">
                                      {fields.map((field, fieldIndex) => {
                                        const formattedValue = formatFieldValue(field.value, field);
                                        return (
                                          <div key={fieldIndex} className="flex gap-2 text-xs py-1 pl-2 border-l-2 border-blue-200 dark:border-blue-800 rounded text-left">
                                            <span className="text-gray-500 shrink-0">Item {(field.array_index ?? 0) + 1}:</span>
                                            <span className="text-gray-800 dark:text-gray-200 break-words whitespace-pre-wrap min-w-0 text-left">
                                              {formattedValue && formattedValue !== 'N/A' ? formattedValue : <span className="italic text-gray-400">—</span>}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No fields available in this section
                    </div>
                  )}
                </div>
              </Card>
              );
            })}
        </div>
      )}

      {!hasDisplayableData && (
        <Card>
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-300 font-medium">No results found</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              No data was returned for this search.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}; 